import { useEffect, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from '@/components/feedback/useToast';
import type { Invitation } from '../types/signup.types';
import { SignupApiError } from '../types/signup.types';
import {
  isValidName,
  isValidPassword,
  isValidPhone,
  useSignupForm,
  type SignupFieldName,
} from '../hooks/use-signup-form';
import { useCountdown } from '@/shared/hooks/use-countdown';
import {
  useCompleteSignupMutation,
  useSendCodeMutation,
  useVerifyCodeMutation,
} from '../hooks/use-signup-mutations';
import { SignupReadonlyFields } from './SignupReadonlyFields';
import { SignupPasswordFields } from './SignupPasswordFields';
import { SignupNameField } from './SignupNameField';
import { SignupPhoneVerification } from './SignupPhoneVerification';
import { SignupInstitutionField } from './SignupInstitutionField';
import { SignupTermsConsent } from './SignupTermsConsent';
import { useSignupTermsQuery } from '../hooks/use-signup-terms';
import { SignupSubmitButton } from './SignupSubmitButton';
import { SignupCompletionNotice } from './SignupCompletionNotice';
import { SignupExpiredCard } from './SignupExpiredCard';
import { InfoModal } from '@/components/feedback/InfoModal';

interface SignupFormProps {
  token: string;
  invitation: Invitation;
}

type SubmitState = 'idle' | 'completed' | 'tokenInvalidated' | 'alreadyRegistered';

export const SignupForm = ({ token, invitation }: SignupFormProps) => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const form = useSignupForm({ initialName: invitation.name });

  // request-otp returns 204 (no expiry in the body) — the FE derives the 5-min
  // OTP countdown locally from the client clock at send time.
  const OTP_TTL_MS = 5 * 60 * 1000;
  const [codeExpiresAt, setCodeExpiresAt] = useState<string | null>(null);
  const secondsLeft = useCountdown(codeExpiresAt);
  const codeRequested = codeExpiresAt !== null;
  const [submitState, setSubmitState] = useState<SubmitState>('idle');

  // US-SU-05 — published Terms for the consent section + the user's per-term selection
  // (termVersionIds the user has agreed to).
  const termsQuery = useSignupTermsQuery(token);
  const [agreedTermIds, setAgreedTermIds] = useState<number[]>([]);

  const signupTerms = termsQuery.data ?? [];
  // Submit gate: terms must have loaded AND every Required term must be agreed. Optional
  // terms never block. Empty list (no published terms) ⇒ gate open — sign-up is NOT blocked
  // (US-SU-05 BA decision: zero published terms allows registration with no consent section).
  const allRequiredTermsAgreed = signupTerms.every(
    (term) => !term.required || agreedTermIds.includes(term.termVersionId),
  );
  const termsGateOpen = termsQuery.isSuccess && allRequiredTermsAgreed;

  const sendCodeMutation = useSendCodeMutation();
  const verifyCodeMutation = useVerifyCodeMutation();
  const completeMutation = useCompleteSignupMutation();

  // Show the success toast (PRD 19 §6.1 mirrors prototype) the moment we flip to
  // completed, so the toast and the in-place notice appear together.
  useEffect(() => {
    if (submitState === 'completed') toast.success(t('signup.toasts.success'));
  }, [submitState, t]);

  const validateField = (field: SignupFieldName) => {
    switch (field) {
      case 'password':
        // Required field — an empty value is a policy violation, so validate on blur
        // even when empty (PRD 19 §8.1). isValidPassword('') is false → shows the rule.
        if (!isValidPassword(form.values.password)) {
          form.setError('password', t('signup.errors.passwordInvalid'));
        }
        break;
      case 'passwordConfirm':
        if (!form.values.passwordConfirm) {
          form.setError('passwordConfirm', t('signup.errors.passwordConfirmRequired'));
        } else if (form.values.password !== form.values.passwordConfirm) {
          form.setError('passwordConfirm', t('signup.errors.passwordMismatch'));
        }
        break;
      case 'name':
        // Required — empty (incl. whitespace-only) → 미입력 message; a non-empty value
        // outside 2~50 chars → length message (PRD 19 §8.1 / §4.1, PRD 21 §5.4.3).
        if (!form.values.name.trim()) {
          form.setError('name', t('signup.errors.nameRequired'));
        } else if (!isValidName(form.values.name)) {
          form.setError('name', t('signup.errors.nameLength'));
        }
        break;
      case 'phone':
        // Required — distinguish empty (미입력) from bad format per PRD 19 §8.1.
        if (!form.values.phone) {
          form.setError('phone', t('signup.errors.phoneRequired'));
        } else if (!isValidPhone(form.values.phone)) {
          form.setError('phone', t('signup.errors.phoneInvalid'));
        }
        break;
      case 'code':
        if (form.values.code && form.values.code.length !== 6) {
          form.setError('code', t('signup.errors.codeFormat'));
        }
        break;
    }
  };

  const handleSendCode = async () => {
    if (!form.canRequestCode) return;
    form.setError('phone', undefined);
    form.setError('code', undefined);
    try {
      // 204 No Content — no body. Derive the countdown deadline locally.
      // Send the invite token so the BE can refuse a phone that differs from the invited one.
      await sendCodeMutation.mutateAsync({
        phone: form.values.phone,
        invitationToken: token,
      });
      setCodeExpiresAt(new Date(Date.now() + OTP_TTL_MS).toISOString());
      form.setField('code', '');
      toast.success(t('signup.toasts.codeSent'));
    } catch (e) {
      if (e instanceof SignupApiError) {
        if (e.code === 'PHONE_INVALID') {
          form.setError('phone', e.fields?.phone ?? t('signup.errors.phoneInvalid'));
          return;
        }
        if (e.code === 'SIGNUP_PHONE_MISMATCH' || e.code === 'PHONE_DUPLICATE') {
          // Anti-enumeration: a phone already in use (PHONE_DUPLICATE) OR not matching the invited
          // number (SIGNUP_PHONE_MISMATCH) shows the SAME generic message, so signup never reveals
          // which check failed. Locale-aware (KO/EN) — never the server's Korean text.
          form.setError('phone', t('errors.phoneRejected', { ns: 'common' }));
          return;
        }
        if (e.code === 'OTP_RATE_LIMITED') {
          form.setError('phone', t('signup.errors.otpRateLimited'));
          return;
        }
        if (e.code === 'SIGNUP_TOKEN_INVALID' || e.code === 'SIGNUP_ALREADY_COMPLETED') {
          setSubmitState('tokenInvalidated');
          return;
        }
      }
      // PRD 19 §8 — the send-code screen has only two system errors: no-response
      // (network) and send failure. A server that responds with any unexpected
      // error (SMS_SEND_FAILED or a bodyless 5xx) is a send failure to the user;
      // only a genuine NETWORK error shows the connection message.
      const isNetwork = e instanceof SignupApiError && e.code === 'NETWORK';
      toast.error(
        isNetwork ? t('signup.toasts.serverError') : t('signup.toasts.smsSendFailed'),
      );
    }
  };

  const handleVerifyCode = async () => {
    if (form.values.code.length !== 6) {
      form.setError('code', t('signup.errors.codeFormat'));
      return;
    }
    form.setError('code', undefined);
    try {
      // 204 No Content — no body. The BE marks the phone verified server-side
      // (keyed by phone); the FE just flips a local boolean and proceeds.
      await verifyCodeMutation.mutateAsync({
        phone: form.values.phone,
        code: form.values.code,
      });
      form.setPhoneVerified(true);
      setCodeExpiresAt(null);
    } catch (e) {
      if (e instanceof SignupApiError) {
        if (e.code === 'OTP_INVALID') {
          form.setError('code', e.fields?.code ?? t('signup.errors.codeInvalid'));
          return;
        }
        if (e.code === 'OTP_EXPIRED') {
          form.setError('code', e.fields?.code ?? t('signup.errors.codeExpired'));
          return;
        }
        if (e.code === 'OTP_RATE_LIMITED') {
          form.setError('code', t('signup.errors.otpRateLimited'));
          return;
        }
        if (e.code === 'PHONE_INVALID') {
          form.setError('phone', e.fields?.phone ?? t('signup.errors.phoneInvalid'));
          return;
        }
        if (e.code === 'SIGNUP_TOKEN_INVALID' || e.code === 'SIGNUP_ALREADY_COMPLETED') {
          setSubmitState('tokenInvalidated');
          return;
        }
      }
      toast.error(t('signup.toasts.serverError'));
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.submitEnabled || !termsGateOpen || completeMutation.isPending) return;
    if (!form.phoneVerified) {
      form.setError('code', t('signup.errors.phoneNotVerified'));
      return;
    }

    try {
      // 204 No Content — derive the completion UI from local form state, not a body.
      // confirmPassword is required and re-checked server-side.
      await completeMutation.mutateAsync({
        token,
        name: form.values.name.trim(),
        password: form.values.password,
        confirmPassword: form.values.passwordConfirm,
        phone: form.values.phone,
        institution: invitation.institution,
        // US-SU-05 — record a decision for every displayed term, incl. Optional non-agreement.
        consents: signupTerms.map((term) => ({
          termVersionId: term.termVersionId,
          agreed: agreedTermIds.includes(term.termVersionId),
        })),
      });
      setSubmitState('completed');
    } catch (e) {
      if (e instanceof SignupApiError) {
        // Field-level — apply every fields.* entry in one pass.
        if (e.fields && Object.keys(e.fields).length > 0) {
          for (const [field, message] of Object.entries(e.fields)) {
            if (
              field === 'password' ||
              field === 'passwordConfirm' ||
              field === 'name' ||
              field === 'phone' ||
              field === 'code'
            ) {
              form.setError(field, message);
            }
          }
          return;
        }
        if (e.code === 'PASSWORD_CONFIRM_MISMATCH') {
          form.setError('passwordConfirm', t('signup.errors.passwordMismatch'));
          return;
        }
        if (e.code === 'PASSWORD_POLICY_VIOLATION') {
          form.setError('password', t('signup.errors.passwordInvalid'));
          return;
        }
        if (e.code === 'PHONE_NOT_VERIFIED') {
          form.setError('code', t('signup.errors.phoneNotVerified'));
          form.setPhoneVerified(false);
          return;
        }
        if (e.code === 'PHONE_DUPLICATE') {
          // Phone already used by another admin. Surfaced inline on the phone field, like the
          // other phone errors; the text is the single shared generic message (locale-aware).
          form.setError('phone', t('errors.phoneRejected', { ns: 'common' }));
          return;
        }
        if (e.code === 'REQUIRED_TERMS_NOT_AGREED') {
          // Defensive: the submit button is already gated on this client-side.
          toast.error(t('signup.terms.requiredNotAgreed'));
          return;
        }
        if (e.code === 'SIGNUP_ALREADY_COMPLETED') {
          // §7.1 Business Logic (already-registered) → Modal; redirect on acknowledge.
          setSubmitState('alreadyRegistered');
          return;
        }
        if (e.code === 'SIGNUP_TOKEN_INVALID') {
          setSubmitState('tokenInvalidated');
          return;
        }
      }
      // PRD 19 §8 — a server-side registration failure (rollback / bodyless 5xx /
      // INTERNAL_ERROR, since the BE has no SIGNUP_FAILED code) shows the signup
      // processing-error message; only a genuine NETWORK error shows the
      // connection message.
      const isNetwork = e instanceof SignupApiError && e.code === 'NETWORK';
      toast.error(
        isNetwork ? t('signup.toasts.serverError') : t('signup.alerts.signupFailed'),
      );
    }
  };

  if (submitState === 'tokenInvalidated') {
    return <SignupExpiredCard />;
  }

  if (submitState === 'alreadyRegistered') {
    return (
      <InfoModal
        open
        title={t('signup.alreadyRegisteredModal.title')}
        body={t('signup.banner.alreadyRegistered')}
        confirmLabel={t('signup.alreadyRegisteredModal.confirm')}
        onClose={() => navigate('/login', { replace: true })}
      />
    );
  }

  if (submitState === 'completed') {
    return <SignupCompletionNotice />;
  }

  const submitting = completeMutation.isPending;

  return (
    <form className="signup-page__form" onSubmit={handleSubmit} noValidate>
      <SignupReadonlyFields invitation={invitation} />

      <SignupPasswordFields
        values={{
          password: form.values.password,
          passwordConfirm: form.values.passwordConfirm,
        }}
        errors={{
          password: form.errors.password,
          passwordConfirm: form.errors.passwordConfirm,
        }}
        onChange={form.setField}
        onBlur={validateField}
        disabled={submitting}
      />

      <SignupNameField
        value={form.values.name}
        error={form.errors.name}
        onChange={(v) => form.setField('name', v)}
        onBlur={() => validateField('name')}
        disabled={submitting}
      />

      <SignupPhoneVerification
        phone={form.values.phone}
        code={form.values.code}
        phoneError={form.errors.phone}
        codeError={form.errors.code}
        canRequestCode={form.canRequestCode}
        codeRequested={codeRequested || form.phoneVerified}
        secondsLeft={secondsLeft}
        phoneVerified={form.phoneVerified}
        sending={sendCodeMutation.isPending}
        verifying={verifyCodeMutation.isPending}
        onPhoneChange={(v) => {
          form.setField('phone', v);
          setCodeExpiresAt(null);
        }}
        onCodeChange={(v) => form.setField('code', v)}
        onPhoneBlur={() => validateField('phone')}
        onCodeBlur={() => validateField('code')}
        onSendCode={handleSendCode}
        onVerifyCode={handleVerifyCode}
        disabled={submitting}
      />

      <SignupInstitutionField defaultInstitution={invitation.institution} />

      <SignupTermsConsent
        terms={termsQuery.data ?? []}
        loading={termsQuery.isLoading}
        error={termsQuery.isError}
        agreedIds={agreedTermIds}
        onChange={setAgreedTermIds}
        disabled={submitting}
      />

      <SignupSubmitButton disabled={!form.submitEnabled || !termsGateOpen} loading={submitting} />
    </form>
  );
};
